import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Input,
  Drawer,
  Skeleton,
  Switch,
  Typography,
  Empty,
  Flex,
  Form,
  Spin,
  message,
  Space,
  Popconfirm,
  Select,
} from 'antd';
import type {
  ColumnsType,
  TablePaginationConfig,
  FilterValue,
  SorterResult,
  TableCurrentDataSource,
} from 'antd/es/table/interface';
import { EditOutlined } from '@ant-design/icons';
import MenuItemForm from '../components/menuitems/menuitemform';
import RecipeItems from '../components/menuitems/recipeitems';
import ImageWithSpinner from '../components/content/imagewithspinner';
import FormButton from '../components/common/formbutton';
import { useAuth } from '../auth/authcontext';
import { getKdsLocationsForMenu, getMenuHighlightsById, getMenuTraitsForMenu, getPagedMenuItems, updateMenuItem } from '../services/menuservice';
import { getMenuOptionsForMenu } from '../services/menuoptiongroupservice';
import { getMenuAddOnsForMenu } from '../services/menuaddonservice';

const { Search } = Input;

interface MenuItem {
  guid: string;
  upc: string;
  posCode: string;
  jdeProductId?: string;
  name: string;
  receiptName: string;
  active: boolean;
  imageName?: string;
  signatureItem?: boolean;
  kdsName?: string;
  kdsInstructions?: string;
  itemType: number;
  tag?: string;
}

interface SortInfo {
  columnKey?: string;
  order?: 'ascend' | 'descend';
}

interface MenuItemFormValues {
  imageName?: string;
  signatureItem?: boolean;
  tag?: string;
  menuOptionGroups?: string[];
  addOnGroups?: string[];
  traits?: string[];
  kdsName?: string;
  kdsInstructions?: { name: string }[];
  highlights?: { name: string }[];
  kdsLocations?: string[];
}

const MenuItems: React.FC = () => {
  const [searchText, setSearchText] = useState<string>('');
  const [editDrawerVisible, setEditDrawerVisible] = useState<boolean>(false);
  const [recipeDrawerVisible, setRecipeDrawerVisible] = useState<boolean>(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState<MenuItem | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [drawerLoading, setDrawerLoading] = useState<boolean>(false);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [totalMenuItems, setTotalMenuItems] = useState<number>(0);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sortedInfo, setSortedInfo] = useState<SortInfo>({});

  const [menuItemForm] = Form.useForm<MenuItemFormValues>();
  const [previewImageUrl, setPreviewImageUrl] = useState<string>('');
  const [messageApi, contextHolder] = message.useMessage();
  const { userId } = useAuth();
  const userGuid = userId || null;
  const [lastUpdatedGuid, setLastUpdatedGuid] = useState<string | null>(null);
  const [itemType, setItemType] = useState<number>(4); //Default Menu

  const fetchMenuItems = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = {
        pageNumber: currentPage,
        pageSize: pageSize,
        itemType: itemType,
        ...(searchText && { searchText }),
      };

      if (sortedInfo.columnKey && sortedInfo.order) {
        params.sortBy = sortedInfo.columnKey;
        params.sortOrder = sortedInfo.order === 'ascend' ? 'asc' : 'desc';
      }

      const data = await getPagedMenuItems(params);
      setMenuItems(data.data);
      setTotalMenuItems(data.totalCount);
    } catch (error) {
      console.error('Error fetching Paged Menu items:', error);
      messageApi.error('Failed to load menu items. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchText, sortedInfo, messageApi, itemType]);

  useEffect(() => {
    fetchMenuItems();
  }, [fetchMenuItems]);

  useEffect(() => {
    if (lastUpdatedGuid) {
      const rowElement = document.querySelector(
        `.ant-table-row[data-row-key="${lastUpdatedGuid}"]`
      ) as HTMLElement;

      if (rowElement) {
        // scroll into view
        rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // highlight row briefly
        rowElement.style.transition = 'background-color 0.5s ease';
        rowElement.style.backgroundColor = '#e6e6e6';

        setTimeout(() => {
          rowElement.style.backgroundColor = '';
        }, 1500);
      }
      setLastUpdatedGuid(null);
    }
  }, [menuItems, lastUpdatedGuid]);

  const fetchMenuData = useCallback(async (guid: string) => {
    if (!guid) return {};

    try {
      const [optionsRes, addOnsRes, traitsRes, highlightsRes, kdsRes] = await Promise.all([
        getMenuOptionsForMenu(guid),
        getMenuAddOnsForMenu(guid),
        getMenuTraitsForMenu(guid),
        getMenuHighlightsById(guid),
        getKdsLocationsForMenu(guid),
      ]);

      return {
        selectedMenuOptions: [...new Set(optionsRes.map((item: any) => item.menuOptionGroupGuid))]
          .filter((guid): guid is string => typeof guid === 'string' && guid !== '00000000-0000-0000-0000-000000000000'),
        selectedAddOns: [...new Set(addOnsRes.map((item: any) => item.menuAddOnGroupGuid))]
          .filter((guid): guid is string => typeof guid === 'string'),
        selectedTraits: [...new Set(traitsRes.map((item: any) => item.guid))]
          .filter((guid): guid is string => typeof guid === 'string'),
        selectedHighlights: highlightsRes as { name: string }[],
        selectedKdsLocations: [...new Set(kdsRes.map((item: any) => item.kdsLocation))]
          .filter((loc): loc is string => typeof loc === 'string'),
      };

    } catch (error) {
      console.error(`Error fetching selected menu data for menu item ${guid}`, error);
      setDrawerLoading(false);
      messageApi.error('Failed to load menu data.');
      return {};
    }
  }, [ messageApi]);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<MenuItem> | SorterResult<MenuItem>[],
    _extra: TableCurrentDataSource<MenuItem>
  ) => {
    if (pagination.current !== currentPage) {
      setCurrentPage(pagination.current || 1);
    }
    if (pagination.pageSize !== pageSize) {
      setPageSize(pagination.pageSize || 10);
      setCurrentPage(1);
    }

    if (!Array.isArray(sorter) && sorter.columnKey && sorter.order) {
      setSortedInfo({ columnKey: sorter.columnKey as string, order: sorter.order });
    } else {
      setSortedInfo({});
    }
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
    setSortedInfo({});
  };

  const handleFormValuesChange = (
    changedValues: Partial<MenuItemFormValues>,
    _allValues: MenuItemFormValues
  ) => {
    if (changedValues.imageName !== undefined) {
      setPreviewImageUrl(changedValues.imageName);
    }
  };

  const handleEdit = async (menuItem: MenuItem) => {
    try {
      setEditDrawerVisible(true);
      setDrawerLoading(true);
      const values = await fetchMenuData(menuItem.guid);
      menuItemForm.resetFields();
      menuItemForm.setFieldsValue({
        imageName: menuItem.imageName,
        signatureItem: menuItem.signatureItem ?? false,
        tag: menuItem.tag,  
        menuOptionGroups: values.selectedMenuOptions,
        addOnGroups: values.selectedAddOns,
        traits: values.selectedTraits,
        highlights: values.selectedHighlights,
        kdsLocations: values.selectedKdsLocations,
        kdsName: menuItem.kdsName,
        kdsInstructions: menuItem.kdsInstructions
          ? menuItem.kdsInstructions.split('\n').map(i => ({ name: i }))
          : [],
      });
      setPreviewImageUrl(menuItem.imageName || '');
      setSelectedMenuItem(menuItem);
    } catch (error) {
      console.error('Error during edit setup:', error);
    } finally {
      setTimeout(() => setDrawerLoading(false), 500);
    }
  };

  const handleRecipe = (menuItem: MenuItem) => {
    setDrawerLoading(true);
    setSelectedMenuItem(menuItem);
    setRecipeDrawerVisible(true);
    setTimeout(() => setDrawerLoading(false), 500);
  };

  const handleEditDrawerClose = () => {
    setEditDrawerVisible(false);
    setSelectedMenuItem(null);
  };

  const handleRecipeDrawerClose = () => {
    setRecipeDrawerVisible(false);
    setSelectedMenuItem(null);
  };

  const handleSaveEdit = async () => {
    try {
      const values = await menuItemForm.validateFields();
      setSaveLoading(true);

      const highlights = values.highlights?.map((h) => h.name) || [];
      const instructionsArray = values.kdsInstructions || [];
      const joinedInstructions = instructionsArray
        .map(i => i.name) // extract the text
        .join('\n');      // line breaks

      const menuUpdateData = {
        guid: selectedMenuItem?.guid,
        createdBy: userGuid,
        imageName: values.imageName,
        signatureItem: values.signatureItem,
        tag: values.tag,
        menuOptionSelections: values.menuOptionGroups,
        addOnsSelections: values.addOnGroups,
        traitSelections: values.traits,
        highlightSelections: highlights,
        kdsLocationSelections: values.kdsLocations,
        kdsName: values.kdsName,
        kdsInstructions: joinedInstructions,       
      };

      await updateMenuItem(menuUpdateData);
      await fetchMenuItems();
      successMessage();
      setLastUpdatedGuid(selectedMenuItem?.guid || null);
    } catch (error) {
      errorMessage();
      console.error('There was an error updating the menu item!', error);
    } finally {
      setSaveLoading(false);
      setEditDrawerVisible(false);
      setSelectedMenuItem(null);
    }
  };

  const successMessage = () => {
    messageApi.success('Menu item updated successfully.');
  };

  const errorMessage = () => {
    messageApi.error('Something went wrong while updating menu item.');
  };

  // const handleToggleActive = async (guid: string, isActive: boolean) => {
  //   try {
  //     setSaveLoading(true);

  //     await axios.put(`${baseUrl}/api/Menu/UpdateActiveStatus`, {
  //       guid,
  //       active: isActive,
  //       updatedBy: userGuid,
  //     });

  //     await fetchMenuItems();
  //     messageApi.success(`Menu item ${isActive ? 'activated' : 'deactivated'} successfully`);
  //     setLastUpdatedGuid(guid);
  //   } catch (error) {
  //     console.error('Error toggling active state:', error);
  //     messageApi.error('Failed to update active state. Please try again.');
  //   } finally {
  //     setSaveLoading(false);
  //   }
  // };

  const columns: ColumnsType<MenuItem> = [
    {
      title: 'Pos Code',
      dataIndex: 'posCode',
      key: 'posCode',
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sortedInfo.columnKey === 'posCode' ? sortedInfo.order : null,
    },
    {
      title: 'Product Id',
      dataIndex: 'jdeProductId',
      key: 'jdeProductId',
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sortedInfo.columnKey === 'jdeProductId' ? sortedInfo.order : null,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sortedInfo.columnKey === 'name' ? sortedInfo.order : null,
    },
    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      render: (active: boolean) => <Switch checked={active} size="small" disabled />,
    },
    // {
    //   title: 'Active',
    //   dataIndex: 'active',
    //   key: 'active',
    //   sorter: true,
    //   sortDirections: ['ascend', 'descend'],
    //   sortOrder: sortedInfo.columnKey === 'active' ? sortedInfo.order : null,
    //   render: (active: boolean, record: MenuItem) => {
    //     const confirmMessage = active
    //       ? "Are you sure you want to make this item inactive?"
    //       : "Are you sure you want to mark this item as active?";

    //     return (
    //       <Popconfirm
    //         title={confirmMessage}
    //         onConfirm={() => handleToggleActive(record.guid, !active)}
    //         okText="Yes"
    //         cancelText="No"
    //       >
    //         <Switch checked={active} size="small" />
    //       </Popconfirm>
    //     );
    //   },
    // },
    {
      title: 'Action',
      key: 'action',
      render: (_text, record) => (
        <Space>
          <FormButton title="Edit" onClick={() => handleEdit(record)} />        
          {record.itemType === 4 && (
            <FormButton title="Recipe" onClick={() => handleRecipe(record)} />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {contextHolder}
      <Typography.Title>Menu Items</Typography.Title>     
      <Space style={{ marginBottom: 16 }}>
        <Search
          placeholder={itemType === 4 ? "Search menu items" : "Search C-store items"}
          onSearch={handleSearch}
          onChange={(e) => {
            if (!e.target.value) {
              handleSearch('');
            }
          }}
          style={{ width: 300 }}
          allowClear
        />

        <Select
          value={itemType}
          onChange={(value) => {
            setItemType(value);
            setCurrentPage(1);
          }}
          style={{ width: 200 }}
          options={[
            { label: 'Menu Items', value: 4 },
            { label: 'C-Store Items', value: 0 },
          ]}
        />
      </Space>

      {loading ? (
        <Skeleton active />
      ) : (
        <Table
          dataSource={menuItems}
          columns={columns}
          rowKey="guid"
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalMenuItems,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
          onChange={handleTableChange}
        />
      )}

      <Drawer
        title={
          <>
            <EditOutlined /> {selectedMenuItem?.name ?? ''}
          </>
        }
        placement="right"
        closable={false}
        onClose={handleEditDrawerClose}
        open={editDrawerVisible}
        width={800}
        styles={{
          header: {
            color: 'white',
            backgroundColor: 'var(--color-primary)',
            fontWeight: 350,
            fontSize: '14px',
            border: 0,
            minHeight: '40px',
          },
        }}
      >
        {drawerLoading ? (
          <Skeleton active />
        ) : selectedMenuItem && (
          <Spin tip="Updating..." spinning={saveLoading}>
            <Flex justify="center" style={{ marginBottom: '10px' }}>
              <div style={{ width: '180px', height: '180px' }}>
                {previewImageUrl ? (
                  <ImageWithSpinner imageUrl={previewImageUrl} />
                ) : (
                  <Empty description="Image Not Added" />
                )}
              </div>
            </Flex>
            <MenuItemForm
              form={menuItemForm}
              onCancel={handleEditDrawerClose}
              onSave={handleSaveEdit}
              onValuesChange={handleFormValuesChange}
              menuItemGuid={selectedMenuItem.guid}
            />
          </Spin>
        )}
      </Drawer>

      <Drawer
        title={`Recipe for ${selectedMenuItem?.name ?? ''}`}
        placement="right"
        closable={true}
        onClose={handleRecipeDrawerClose}
        open={recipeDrawerVisible}
        width={1000}
        styles={{
          header: {
            color: 'white',
            backgroundColor: 'var(--color-primary)',
            fontWeight: 350,
            fontSize: '14px',
            border: 0,
            minHeight: '40px',
          },
        }}
      >
        {drawerLoading ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : selectedMenuItem && (
          <RecipeItems menuItem={selectedMenuItem} />
        )}
      </Drawer>
    </div>
  );
};

export default MenuItems;